import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, SecurityContext, signal, viewChild , ElementRef, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FileFormat } from '../../services/export/export.domain';
import { ExportService } from '../../services/export/export.service';
import type { ExportEvent } from '../../services/export/export.domain';

/**
 * This component is used to export (download) a file from a backend server.
 *
 * Usage:
 *
 *  - from invoker (host page):
 *  1. add <td-wb-export></td-wb-export>
 *  2. call exportService.exportData(exportParameters), where exportParameters are defined as { [key: string]: any | Array<any> }
 *
 */
@Component({
  imports: [
    CommonModule,
    FormsModule
  ],
  selector: 'td-wb-export',
  templateUrl: 'export.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.display]': 'display()'
  }

})
export class ExportComponent {
  readonly CONSUMER_NAME_FIELD: string = 'x-consumer-name';
  readonly display: WritableSignal<string> = signal<string>('none');
  readonly exportForm: () => ElementRef<HTMLFormElement> = viewChild.required<ElementRef<HTMLFormElement>>('exportForm');

  readonly exportServiceUrl: WritableSignal<string | null> = signal<string | null>(null);
  readonly exportRequest: WritableSignal<string> = signal<string>('');
  readonly exportParams: WritableSignal<string> = signal<string>('');
  readonly consumerName: WritableSignal<string> = signal<string>('');

  private readonly exportService: ExportService = inject(ExportService);
  private readonly sanitizer: DomSanitizer = inject(DomSanitizer);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  constructor(){
    this.exportService.onExport.pipe(takeUntilDestroyed()).subscribe((exportEvent: ExportEvent): void => {
      this.onExport(exportEvent);
    });
  }

  getExportFileFormat(fileFormat?: FileFormat): string {
    if (fileFormat !== null && fileFormat !== undefined) {
      return fileFormat;
    }
    return FileFormat.CSV as string;
  }

  private onExport(exportEvent: ExportEvent): void {
    const request = exportEvent.exportRequest;
    this.exportRequest.set(
      JSON.stringify({
        type: request.exportType,
        fileFormat: this.getExportFileFormat(request.fileFormat),
        contentDisposition: request.contentDisposition
      })
    );

    this.exportParams.set(JSON.stringify(request.exportParams));
    this.consumerName.set(exportEvent.restConsumerName);
    this.exportServiceUrl.set(this.sanitizer.sanitize(SecurityContext.URL, exportEvent.exportServiceUrl));
    this.changeDetectorRef.detectChanges();
    this.exportForm().nativeElement.submit();
  }

}
